import { useState } from 'react';
import { Separator } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { ClientFormDrawer } from '@/components/ClientForm/ClientFormDrawer';
import { ClientPaymentSection } from '@/components/ClientPaymentSection/ClientPaymentSection';
import ContactDetails from '@/components/ContactDetails';
import { UpdateStore } from '@/components/Store/UpdateStore';
import config from '@/config';
import Accordion from '@/design_system/Accordion';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCalendar from '@/icons/Calendar.svg';
import IconEdit from '@/icons/Edit.svg';
import IconFile from '@/icons/File.svg';
import IconStore from '@/icons/Store.svg';
import IconPerson from '@/icons/User.svg';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { isArchived, isInInitialTransitStep, isInTransitStep } from '@/models/request';
import { useUpdateExternalStore } from '@/models/store';
import { shouldShowClientPaymentSection, useClientPaymentTransactions } from '@/models/transaction';
import { useClientToken, useStoreToken } from '@/services/auth';
import { formatDate } from '@/utils/date';
import useViewPort from '@/utils/useViewport';

import Invoice from './Invoice';

export const RequestInfo = () => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { request } = useBrandRequestContext();
  const clientToken = useClientToken();
  const storeToken = useStoreToken();
  const { data: transactions = [] } = useClientPaymentTransactions(request.id);

  const requestInfoLabel = t({ id: 'brand.request-info.info', message: 'Request information' });
  const clientInfoLabel = clientToken
    ? t({ id: 'brand.request-info.my-info', message: 'My information' })
    : t({ id: 'brand.request-info.client-info', message: 'Client information' });
  const storeInfoLabel = t({ id: 'brand.request-info.store', message: 'Store' });

  const showClientPaymentSection = shouldShowClientPaymentSection({
    canSeeClientPrice: !!(clientToken || storeToken) && !!request.client,
    transactionsCount: transactions.length,
    request,
  });

  if (isMobile) {
    return (
      <Box padding="0" style={{ marginTop: '1rem' }}>
        <div aria-label={requestInfoLabel}>
          <Accordion
            trigger={
              <Stack row gap="0.25rem" alignItems="center">
                <IconFile style={{ fontSize: '1.25rem' }} />
                {requestInfoLabel}
              </Stack>
            }
          >
            <RequestDetails />
          </Accordion>
        </div>

        {!!showClientPaymentSection && (
          <>
            <Separator />
            <ClientPaymentSection
              request={request}
              transactions={transactions}
              canViewTransactions={false}
              openByDefault={false}
              isClientView={!!clientToken}
            />
          </>
        )}

        {request.client && (
          <>
            <Separator />
            <div aria-label={clientInfoLabel}>
              <Accordion
                trigger={
                  <Stack row gap="0.25rem" alignItems="center">
                    <IconPerson style={{ fontSize: '1.25rem' }} />
                    {clientInfoLabel}
                  </Stack>
                }
              >
                <ClientInfo />
              </Accordion>
            </div>
          </>
        )}

        {request.store && (
          <>
            <Separator />
            <div aria-label={storeInfoLabel}>
              <Accordion
                trigger={
                  <Stack row gap="0.25rem" alignItems="center">
                    <IconStore style={{ fontSize: '1.25rem' }} />
                    <Trans id="brand.request-info.store">Store</Trans>
                  </Stack>
                }
              >
                <StoreInfo />
              </Accordion>
            </div>
          </>
        )}
      </Box>
    );
  }

  return (
    <Stack gap="0.5rem">
      <div style={{ padding: '1.5rem 0 0.5rem' }} aria-label={requestInfoLabel}>
        <RequestDetails />
      </div>

      {showClientPaymentSection && (
        <>
          <Separator />
          <ClientPaymentSection
            request={request}
            transactions={transactions}
            canViewTransactions={false}
            openByDefault
            isClientView={!!clientToken}
          />
        </>
      )}

      {request.client && (
        <>
          <Separator />
          <div aria-label={clientInfoLabel}>
            <Accordion
              openByDefault
              trigger={<Paragraph weight="medium">{clientInfoLabel}</Paragraph>}
            >
              <ClientInfo />
            </Accordion>
          </div>
        </>
      )}

      {request.store && (
        <>
          <Separator />
          <div aria-label={storeInfoLabel}>
            <Accordion
              openByDefault
              trigger={<Paragraph weight="medium">{storeInfoLabel}</Paragraph>}
            >
              <StoreInfo />
            </Accordion>
          </div>
        </>
      )}
    </Stack>
  );
};

const RequestDetails = () => {
  const { request } = useBrandRequestContext();
  const clientToken = useClientToken();

  return (
    <Stack gap="0.75rem">
      <Stack>
        <InlineText size="200" color="secondary">
          <Trans id="brand.request-info.reference">Reference</Trans>
        </InlineText>
        <InlineText weight="medium">{request.reference}</InlineText>
      </Stack>

      <Stack>
        <InlineText size="200" color="secondary">
          <Trans id="brand.request-info.creation-date">Creation date</Trans>
        </InlineText>
        <Stack row alignItems="center" gap="0.25rem">
          <IconCalendar style={{ fontSize: '1rem' }} />
          <InlineText weight="medium">
            {formatDate(new Date(request.referencedAt ?? request.createdAt), {
              dateStyle: 'medium',
            })}
          </InlineText>
        </Stack>
      </Stack>

      {!!clientToken && <Invoice />}
    </Stack>
  );
};

const ClientInfo = () => {
  const { t } = useLingui();
  const clientToken = useClientToken();
  const storeToken = useStoreToken();
  const { request, workflow, view } = useBrandRequestContext();

  const [isOpenClientFormDrawer, setIsOpenClientFormDrawer] = useState(false);

  const canEdit =
    !isArchived(request) && !(isInTransitStep(request) && !isInInitialTransitStep(request));

  if (!request.client || !view.client.enabled) {
    return null;
  }

  return (
    <>
      <Stack gap="1rem">
        <ContactDetails
          type="client"
          name={request.client.name}
          email={request.client.email}
          address={request.client.address}
          phone={request.client.phone}
          deliveryChoice={request.client.deliveryOption}
          documents={
            request.client && request.store && workflow?.config.dropOffReceipt?.enabled
              ? [
                  {
                    label: view.client.dropoffSignature.enabled
                      ? t({
                          id: 'brand.request-info.client-info.signed-document',
                          message: 'Signed drop-off receipt',
                        })
                      : t({
                          id: 'brand.request-info.client-info.document',
                          message: 'Drop-off receipt',
                        }),
                    url:
                      config.apiUrl +
                      `/requests/${request.id}/drop-off-receipt?` +
                      (clientToken
                        ? `clientToken=${clientToken}`
                        : storeToken
                          ? `storeToken=${storeToken}`
                          : ''),
                  },
                ]
              : undefined
          }
          size="small"
        />
        {canEdit && (
          <Button
            variant="secondary-brand"
            size="small"
            onPress={() => setIsOpenClientFormDrawer(true)}
          >
            <IconEdit />
            <Trans id="brand.request-info.client-info.edit">Edit</Trans>
          </Button>
        )}
      </Stack>
      <ClientFormDrawer
        isOpen={isOpenClientFormDrawer}
        setIsOpen={setIsOpenClientFormDrawer}
        disableEmailEdit={!!clientToken}
        hideVip
        hideLanguage={!!clientToken}
        hideDeliveryOption={!!clientToken}
        variant="brand"
        size="large"
      />
    </>
  );
};

const StoreInfo = () => {
  const { request } = useBrandRequestContext();
  const storeToken = useStoreToken();

  const { mutateAsync: updateExternalStore, isPending: isUpdateExternalStorePending } =
    useUpdateExternalStore();

  if (!request.store) {
    return null;
  }

  return (
    <Stack gap="1rem">
      <ContactDetails
        type="store"
        name={request.store.name}
        email={request.store.address?.contactEmail}
        address={request.store.address}
        phone={request.store.contactInfo?.phone}
        openingHours={request.store.storeConfig?.openingHours}
        size="small"
      />
      {!!storeToken && (
        <UpdateStore
          store={{ ...request.store, groups: [] }}
          isPending={isUpdateExternalStorePending}
          updateStore={async (store) => {
            await updateExternalStore(store);
          }}
          variant="brand"
          size="large"
          trigger={
            <Button variant="secondary-brand" size="small">
              <IconEdit />
              <Trans id="brand.request-info.store-info.edit">Edit</Trans>
            </Button>
          }
        />
      )}
    </Stack>
  );
};
